import { FC } from 'react';
import { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Phone, Calendar, FileText, TrendingUp, Target, MessageSquare, Clock, User, Building, MapPin } from 'lucide-react';
import { KPICard } from '@/features/marketing/components/KPICard';

import { UserDashboardProfile, UserMetric, UserInteraction } from '@/features/users/types';

const UserDashboard: FC = () => {
  const [selectedUser, setSelectedUser] = useState<UserDashboardProfile | null>(null);
  const [interactions, setInteractions] = useState<UserInteraction[]>([]);
  const [loading, setLoading] = useState(true);

  // Listen for user selection events
  useEffect(() => {
    const handleUserSelection = (event: CustomEvent) => {
      setSelectedUser(event.detail.user);
    };

    window.addEventListener('userSelection', handleUserSelection as EventListener);

    return () => {
      window.removeEventListener('userSelection', handleUserSelection as EventListener);
    };
  }, []);

  // Mock user interactions data
  useEffect(() => {
    if (selectedUser) {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setInteractions([
          {
            id: '1',
            type: 'email',
            title: 'Follow-up Email',
            description: 'Sent product pricing information',
            date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'opened',
            user: 'John Smith'
          },
          {
            id: '2',
            type: 'call',
            title: 'Sales Call',
            description: 'Discussed implementation requirements',
            date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'completed',
            user: 'Sarah Johnson'
          },
          {
            id: '3',
            type: 'meeting',
            title: 'Product Demo',
            description: 'Live demonstration of platform features',
            date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'completed',
            user: 'Mike Wilson'
          },
          {
            id: '4',
            type: 'note',
            title: 'Internal Note',
            description: 'User is interested in enterprise features',
            date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
            user: 'Jane Davis'
          },
        ]);
        setLoading(false);
      }, 500);
    }
  }, [selectedUser]);

  // Mock user metrics
  const userMetrics: UserMetric[] = [
    {
      label: 'Total Interactions',
      value: 24,
      change: 12,
      icon: <MessageSquare className="w-5 h-5" />
    },
    {
      label: 'Response Rate',
      value: '85%',
      change: 8,
      icon: <TrendingUp className="w-5 h-5" />
    },
    {
      label: 'Last Contact',
      value: '2 days ago',
      icon: <Clock className="w-5 h-5" />
    },
    {
      label: 'Deal Status',
      value: 'Active',
      icon: <Target className="w-5 h-5" />
    },
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays === 0) return 'Today';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getInteractionIcon = (type: UserInteraction['type']) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'call': return <Phone className="w-4 h-4" />;
      case 'meeting': return <Calendar className="w-4 h-4" />;
      case 'note': return <FileText className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status?: UserInteraction['status']) => {
    switch (status) {
      case 'sent': return 'bg-blue-100 text-blue-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'opened': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleBackToUsers = () => {
    // Clear user selection
    window.dispatchEvent(new CustomEvent('userSelection', {
      detail: { user: null, isSelected: false }
    }));
  };

  if (!selectedUser) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-8">
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No User Selected</h2>
          <p className="text-gray-600 mb-6">Select a user from the External Users sidebar to view their details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBackToUsers}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-medium">
              {selectedUser.name.split(' ').map(word => word.charAt(0).toUpperCase()).join('').slice(0, 2)}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{selectedUser.name}</h1>
              <p className="text-gray-600">{selectedUser.email}</p>
              {selectedUser.company && (
                <p className="text-sm text-gray-500">{selectedUser.company}</p>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Send Message
            </button>
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
              Edit Profile
            </button>
          </div>
        </div>
      </div>

      {/* User Contact Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-2 text-gray-600">
            <Mail className="w-4 h-4" />
            <span className="text-sm">{selectedUser.email}</span>
          </div>
          {selectedUser.phone && (
            <div className="flex items-center gap-2 text-gray-600">
              <Phone className="w-4 h-4" />
              <span className="text-sm">{selectedUser.phone}</span>
            </div>
          )}
          {selectedUser.company && (
            <div className="flex items-center gap-2 text-gray-600">
              <Building className="w-4 h-4" />
              <span className="text-sm">{selectedUser.company}</span>
            </div>
          )}
          {(selectedUser.city || selectedUser.country) && (
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">{[selectedUser.city, selectedUser.country].filter(Boolean).join(', ')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {userMetrics.map((metric, index) => (
          <KPICard
            key={index}
            kpi={{
              title: metric.label,
              value: metric.value.toString(),
              subtitle: metric.change ? `${metric.change > 0 ? '+' : ''}${metric.change}%` : '',
            }}
          />
        ))}
      </div>

      {/* Recent Interactions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Interactions</h2>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2 animate-pulse"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : interactions.length > 0 ? (
            <div className="space-y-4">
              {interactions.map((interaction) => (
                <div
                  key={interaction.id}
                  className="p-4 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                        {getInteractionIcon(interaction.type)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{interaction.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{interaction.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>{formatDate(interaction.date)}</span>
                          {interaction.user && <span>• {interaction.user}</span>}
                        </div>
                      </div>
                    </div>
                    {interaction.status && (
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(interaction.status)}`}>
                        {interaction.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No interactions yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;